import { useMemo } from 'react'
import useTheme from '../../../../hooks/useTheme'

/*
 * Chart colours, all from the active theme.
 *
 * The first two categories are the theme's own primary and secondary. The
 * others are the primary turned round the colour wheel at the primary's own
 * saturation and lightness, so a sixth slice of a donut still looks like it
 * belongs to the same app, in either mode, with any accent the user picked.
 *
 * Statuses (`success`, `warning`, `danger`, `info`) keep their meaning across
 * themes — a cancelled order is red whatever the accent is — but are drawn at a
 * lightness that suits the mode.
 *
 * In config a colour is a token — 'primary', 'secondary', 'success',
 * 'warning', 'danger', 'info', 'neutral' — or 'category1'…'category8'. A raw
 * '#hex' is honoured too, though it will not follow a theme change.
 */

const hexToHsl = (hex) => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex ?? '')
    if (!m) return { h: 220, s: 70, l: 50 }
    const [r, g, b] = m.slice(1).map(x => parseInt(x, 16) / 255)
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    const l = (max + min) / 2
    if (max === min) return { h: 0, s: 0, l: l * 100 }
    const d = max - min
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    return { h: h * 60, s: s * 100, l: l * 100 }
}

const hsl = ({ h, s, l }) => `hsl(${Math.round(((h % 360) + 360) % 360)} ${Math.round(s)}% ${Math.round(l)}%)`

// Rotations chosen to sit apart from each other and from the start
const ROTATIONS = [150, 45, 270, 200, 320, 100]

const STATUS_HUES = { success: 150, warning: 40, danger: 4, info: 210 }

export const buildPalette = (theme) => {
    const dark = theme.mode === 'dark'
    const primary = theme.chart?.fillColor ?? theme.btn?.primary
    const secondary = theme.secondary?.main ?? primary
    const base = hexToHsl(primary)
    // A very pale or very grey accent would give a washed-out set
    const s = Math.max(45, Math.min(base.s, 80))
    const l = dark ? Math.max(58, Math.min(base.l, 68)) : Math.max(44, Math.min(base.l, 56))

    const categories = [primary, secondary, ...ROTATIONS.map(r => hsl({ h: base.h + r, s, l }))]
    const status = Object.fromEntries(Object.entries(STATUS_HUES).map(([k, h]) => [k, hsl({ h, s: dark ? 60 : 62, l: dark ? 60 : 46 })]))

    const tokens = {
        primary, secondary, ...status,
        neutral: theme.font?.label ?? '#888',
        ...Object.fromEntries(categories.map((c, i) => [`category${i + 1}`, c])),
    }

    return {
        categories,
        tokens,
        // A token, a raw colour, or — with neither — the index-th category
        color: (token, index = 0) => tokens[token] ?? (typeof token === 'string' && /^(#|rgb|hsl)/.test(token) ? token : categories[index % categories.length]),
        grid: theme.table?.border ?? '#ddd',
        axis: theme.font?.label ?? '#888',
        text: theme.font?.color ?? '#222',
        surface: theme.card?.background ?? '#fff',
        track: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    }
}

const usePalette = () => {
    const { theme } = useTheme()
    return useMemo(() => buildPalette(theme), [theme.mode, theme.chart?.fillColor, theme.secondary?.main])
}

export default usePalette
